import {
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import i18n, { availableLocales } from "../../../../locales/i18n";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { FadeInDown } from "react-native-reanimated";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { useState } from "react";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import customAxios from "../../../../utils/customAxios";
import WebForgetPasswordScreen from "../../../../components/web/WebForgetPasswordScreen";

export default function ForgetPasswordScreen() {
  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebForgetPasswordScreen />;
  }

  const locale = i18n.locale;

  const [showLanguages, setShowLanguages] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      verificationCode: "",
      newPassword: "",
    },
  });

  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code and new password
  const [showPassword, setShowPassword] = useState(false);

  const handleSendCode = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/users/forgot-password/", {
        email: data.email,
      });

      Toast.show(response.data?.message || i18n.t("verificationCodeSent"), {
        type: "success",
      });

      // Move to step 2 (verification code and new password)
      setStep(2);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("errorOccurred");

      Toast.show(errorMessage, {
        type: "danger",
      });
    } finally {
      setButtonSpinner(false);
    }
  };

  const handleResetPassword = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/users/reset-forgot-password", {
        verificationCode: data.verificationCode,
        email: data.email,
        newpassword: data.newPassword,
      });

      Toast.show(response.data?.message || i18n.t("passwordResetSuccess"), {
        type: "success",
      });

      // Navigate to login page
      router.replace("/(routes)/(auth)/login");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("errorOccurred");

      Toast.show(errorMessage, {
        type: "danger",
      });
    } finally {
      setButtonSpinner(false);
    }
  };

  const ChangeLang = () => {
    setShowLanguages(true);
  };

  return (
    <View style={{ flex: 1 }}>
      <LinearGradient
        colors={["#004E6D", "#0596D0"]}
        locations={[0, 1]}
        style={{ flex: 1 }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{ flex: 1 }}
          keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
        >
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Top section with logo and curved bottom */}
            <View
              style={{
                alignItems: "center",
                paddingTop: hp("8%"),
                paddingBottom: hp("6%"),
              }}
            >
              <Image
                style={{
                  width: wp("20%"),
                  height: wp("20%"),
                  tintColor: "white",
                  marginBottom: hp("3%"),
                }}
                source={require("../../../../assets/logo-no-bg-white.png")}
                resizeMode="contain"
              />

              {/* White curved section */}
              <View
                style={{
                  backgroundColor: "white",
                  width: wp("100%"),
                  minHeight: hp("75%"),
                  borderTopLeftRadius: 60,
                  borderTopRightRadius: 60,
                  paddingHorizontal: wp("8%"),
                  paddingTop: hp("4%"),
                  marginTop: hp("2%"),
                }}
              >
                {/* Title */}
                <Animated.View entering={FadeInDown.duration(400).delay(100)}>
                  <Text
                    style={{
                      fontSize: 24,
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#004E6D",
                      marginBottom: hp("2%"),
                      fontFamily: "kawkabBold",
                    }}
                  >
                    {step === 1
                      ? i18n.t("forgotPassword")
                      : i18n.t("resetPassword")}
                  </Text>
                </Animated.View>

                {/* Description */}
                <Animated.View entering={FadeInDown.duration(400).delay(200)}>
                  <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    textAlign: "center",
                    marginBottom: hp("4%"),
                    paddingHorizontal: wp("4%"),
                    lineHeight: 24,
                  }}
                >
                  {step === 1
                    ? i18n.t("enterEmailToResetPassword")
                    : i18n.t("enterCodeAndNewPassword")}
                  </Text>
                </Animated.View>

                {/* Email Field - Always visible */}
                <Animated.View entering={FadeInDown.duration(400).delay(300)} style={{ marginBottom: hp("3%") }}>
                  <Text
                    style={{
                      fontSize: 16,
                      color: "#666",
                      marginBottom: hp("1%"),
                      textAlign: locale === "ar" ? "right" : "left",
                    }}
                  >
                    {i18n.t("email")}
                  </Text>
                  <View
                    style={{
                      borderWidth: 1,
                      borderColor: "#E0E0E0",
                      borderRadius: 25,
                      backgroundColor: step === 2 ? "#F0F0F0" : "#F9F9F9",
                      flexDirection: "row",
                      alignItems: "center",
                      paddingHorizontal: wp("4%"),
                      height: 50,
                    }}
                  >
                    <Feather
                      name="mail"
                      size={20}
                      color="#999"
                      style={{ marginRight: 10 }}
                    />
                    <Controller
                      control={control}
                      name="email"
                      rules={{
                        required: i18n.t("mailRequired"),
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: i18n.t("invalidEmail"),
                        },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          style={{
                            flex: 1,
                            textAlign: locale === "ar" ? "right" : "left",
                            fontSize: 16,
                            color: "#000",
                          }}
                          placeholder={i18n.t("enterEmail")}
                          placeholderTextColor="#999"
                          autoCapitalize="none"
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          keyboardType="email-address"
                          editable={step === 1}
                        />
                      )}
                    />
                  </View>
                  {errors.email && (
                    <Text
                      style={{
                        color: "red",
                        textAlign: locale === "ar" ? "right" : "left",
                        marginTop: 5,
                      }}
                    >
                      {errors.email.message}
                    </Text>
                  )}
                </Animated.View>

                {/* Verification Code Field - Only show in step 2 */}
                {step === 2 && (
                  <Animated.View entering={FadeInDown.duration(400).delay(400)} style={{ marginBottom: hp("3%") }}>
                    <Text
                      style={{
                        fontSize: 16,
                        color: "#666",
                        marginBottom: hp("1%"),
                        textAlign: locale === "ar" ? "right" : "left",
                      }}
                    >
                      {i18n.t("enterVerificationCode")}
                    </Text>
                    <View
                      style={{
                        borderWidth: 1,
                        borderColor: "#E0E0E0",
                        borderRadius: 25,
                        backgroundColor: "#F9F9F9",
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: wp("4%"),
                        height: 50,
                      }}
                    >
                      <Feather
                        name="key"
                        size={20}
                        color="#999"
                        style={{ marginRight: 10 }}
                      />
                      <Controller
                        control={control}
                        name="verificationCode"
                        rules={{
                          required: i18n.t("enterVerificationCode"),
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TextInput
                            style={{
                              flex: 1,
                              textAlign: locale === "ar" ? "right" : "left",
                              fontSize: 16,
                              color: "#000",
                            }}
                            placeholder="123456"
                            placeholderTextColor="#999"
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="number-pad"
                            maxLength={6}
                          />
                        )}
                      />
                    </View>
                    {errors.verificationCode && (
                      <Text
                        style={{
                          color: "red",
                          textAlign: locale === "ar" ? "right" : "left",
                          marginTop: 5,
                        }}
                      >
                        {errors.verificationCode.message}
                      </Text>
                    )}
                  </Animated.View>
                )}

                {/* New Password Field - Only show in step 2 */}
                {step === 2 && (
                  <Animated.View entering={FadeInDown.duration(400).delay(500)} style={{ marginBottom: hp("3%") }}>
                    <Text
                      style={{
                        fontSize: 16,
                        color: "#666",
                        marginBottom: hp("1%"),
                        textAlign: locale === "ar" ? "right" : "left",
                      }}
                    >
                      {i18n.t("newPassword")}
                    </Text>
                    <View
                      style={{
                        borderWidth: 1,
                        borderColor: "#E0E0E0",
                        borderRadius: 25,
                        backgroundColor: "#F9F9F9",
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: wp("4%"),
                        height: 50,
                      }}
                    >
                      <Feather
                        name="lock"
                        size={20}
                        color="#999"
                        style={{ marginRight: 10 }}
                      />
                      <Controller
                        control={control}
                        name="newPassword"
                        rules={{
                          required: i18n.t("passwordRequired"),
                          minLength: {
                            value: 5,
                            message: i18n.t("passwordMinLength"),
                          },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TextInput
                            style={{
                              flex: 1,
                              textAlign: locale === "ar" ? "right" : "left",
                              fontSize: 16,
                              color: "#000",
                            }}
                            placeholder="••••••••"
                            placeholderTextColor="#999"
                            autoCapitalize="none"
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            secureTextEntry={!showPassword}
                          />
                        )}
                      />
                      <TouchableOpacity
                        onPress={() => setShowPassword(!showPassword)}
                        style={{ marginLeft: 10 }}
                      >
                        <Feather
                          name={showPassword ? "eye" : "eye-off"}
                          size={20}
                          color="#999"
                        />
                      </TouchableOpacity>
                    </View>
                    {errors.newPassword && (
                      <Text
                        style={{
                          color: "red",
                          textAlign: locale === "ar" ? "right" : "left",
                          marginTop: 5,
                        }}
                      >
                        {errors.newPassword.message}
                      </Text>
                    )}
                  </Animated.View>
                )}

                {/* Action Button */}
                <Animated.View entering={FadeInDown.duration(400).delay(600)}>
                  <TouchableOpacity
                  style={{
                    marginBottom: hp("3%"),
                    borderRadius: 25,
                    overflow: "hidden",
                  }}
                  onPress={handleSubmit(
                    step === 1 ? handleSendCode : handleResetPassword
                  )}
                >
                  <LinearGradient
                    colors={["#004E6D", "#0596D0"]}
                    locations={[0, 1]}
                    style={{
                      paddingVertical: hp("2%"),
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {buttonSpinner ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <Text
                        style={{
                          color: "white",
                          textAlign: "center",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      >
                        {step === 1
                          ? i18n.t("sendCode")
                          : i18n.t("resetPassword")}
                      </Text>
                    )}
                  </LinearGradient>
                </TouchableOpacity>
                </Animated.View>

                {/* Back to previous step - Only show in step 2 */}
                {step === 2 && (
                  <Animated.View entering={FadeInDown.duration(400).delay(700)}>
                    <TouchableOpacity
                    onPress={() => setStep(1)}
                    style={{
                      marginBottom: hp("2%"),
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={{
                        color: "#004E6D",
                        fontSize: 16,
                        textDecorationLine: "underline",
                      }}
                    >
                      {i18n.t("backToPreviousStep")}
                    </Text>
                  </TouchableOpacity>
                  </Animated.View>
                )}

                {/* Links */}
                <Animated.View entering={FadeInDown.duration(400).delay(800)} style={{ alignItems: "center", marginBottom: hp("4%") }}>
                  <TouchableOpacity onPress={() => router.back()}>
                    <Text
                      style={{
                        color: "#004E6D",
                        fontSize: 16,
                        textDecorationLine: "underline",
                        marginBottom: hp("1%"),
                      }}
                    >
                      {i18n.t("backToLogin")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => router.push("/(routes)/(auth)/signup")}
                  >
                    <Text
                      style={{
                        color: "#666",
                        fontSize: 16,
                      }}
                    >
                      {i18n.t("dontHaveAccount")}
                    </Text>
                    </TouchableOpacity>
                </Animated.View>

                {/* Language Selection */}
                <Animated.View entering={FadeInDown.duration(400).delay(900)} style={{ alignItems: "center", marginBottom: hp("2%") }}>
                  {!showLanguages && (
                    <TouchableOpacity
                      onPress={ChangeLang}
                      style={{
                        backgroundColor: "#B0B0B0",
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: wp("8%"),
                        paddingVertical: hp("1.5%"),
                        borderRadius: 10,
                        gap: 8,
                      }}
                    >
                      <MaterialIcons
                        name="expand-more"
                        size={24}
                        color="black"
                      />
                      <Text
                        style={{
                          color: "black",
                          fontSize: 16,
                          fontWeight: "bold",
                        }}
                      >
                        {i18n.t("language")}
                      </Text>
                      <MaterialIcons name="language" size={24} color="black" />
                    </TouchableOpacity>
                  )}

                  {showLanguages && (
                    <View style={{ width: wp("70%") }}>
                      {availableLocales.map((item) => (
                        <TouchableOpacity
                          key={item.code}
                          onPress={() => {
                            setShowLanguages(false);
                            i18n.locale = item.code;
                          }}
                          style={{
                            backgroundColor:
                              i18n.locale === item.code ? "#004E6D" : "#E0E0E0",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingHorizontal: wp("6%"),
                            paddingVertical: hp("1.5%"),
                            borderRadius: 10,
                            marginBottom: hp("1%"),
                            elevation: 2,
                            shadowColor: "#000",
                            shadowOffset: {
                              width: 0,
                              height: 1,
                            },
                            shadowOpacity: 0.22,
                            shadowRadius: 2.22,
                          }}
                        >
                          <Text
                            style={{
                              fontWeight: "bold",
                              textAlign: "center",
                              fontSize: 16,
                              color:
                                i18n.locale === item.code ? "white" : "black",
                            }}
                          >
                            {item.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </Animated.View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </LinearGradient>
    </View>
  );
}
